import { UserFacingError } from '@features/shared/api/client'
import type { MicrosoftCallbackRequest } from '../api/contracts'

export const MICROSOFT_CALLBACK_PATH = '/settings/credentials/microsoft/callback'

const callbackKeys = ['state', 'tenant', 'admin_consent', 'code', 'error', 'error_description']

export function parseMicrosoftCallback(params: URLSearchParams): MicrosoftCallbackRequest {
  const invalid = () =>
    new UserFacingError('The Microsoft connection response is invalid. Start again.')
  if (callbackKeys.some((key) => params.getAll(key).length > 1) || params.has('tenant_id')) {
    throw invalid()
  }

  const state = params.get('state')
  if (!state?.trim()) throw new UserFacingError('The connection request is missing. Start again.')

  // Microsoft can include admin_consent=True even in an error response. Let the
  // server consume the request state without treating any other field as consent.
  if (params.has('error')) {
    const error = params.get('error')
    if (!error?.trim()) throw invalid()
    // Retain only Microsoft's diagnostic identifier. Descriptions can contain
    // account details; do not forward, persist, or render the raw text.
    const aadstsCode = params.get('error_description')?.match(/\b(AADSTS[0-9]{4,10})\b/)?.[1]
    return { state, error, ...(aadstsCode ? { aadsts_code: aadstsCode } : {}) }
  }

  const tenant = params.get('tenant')
  const code = params.get('code')
  const consent = params.get('admin_consent')?.toLowerCase()
  if (
    (params.has('tenant') && !tenant?.trim()) ||
    (params.has('code') && !code?.trim()) ||
    (consent !== undefined && consent !== 'true' && consent !== 'false') ||
    (params.has('code') && params.has('admin_consent'))
  ) {
    throw invalid()
  }

  return {
    state,
    ...(tenant ? { tenant_id: tenant } : {}),
    ...(code ? { code } : {}),
    ...(consent !== undefined ? { admin_consent: consent === 'true' } : {}),
  }
}

/** Hands the browser back to Microsoft for the next sign-in step, leaving no history entry. */
export function continueAtMicrosoft(url: string) {
  window.location.replace(url)
}
